import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { captureException } from '@sentry/angular';
import { determinePublicationStatus } from '@wilson/activities/pipes';
import { PublicationStatus } from '@wilson/interfaces';
import { ShiftsService } from '@wilson/shifts';
import { NzMessageService } from 'ng-zorro-antd/message';
import { firstValueFrom } from 'rxjs';
import {
  SimplifiedShiftDrawerFormService,
  SimplifiedShiftDrawerFormValue,
} from '../../simplified-shift-drawer-form.service';
import { SimplifiedDrawerShift } from '../../types';
import { SimplifiedShiftReloadAndTimelineSyncService } from './simplified-shift-reload-and-timeline-sync.service';
import {
  ReloadedShift,
  SimplifiedShiftSaveStateChange,
} from './simplified-shift-save.types';

export const SAVEABLE_FIELDS_WITH_PUBLICATION_STATUS_UPDATE = [
  'organizationalUnitId',
] as const satisfies readonly (keyof SimplifiedShiftDrawerFormValue)[];

export const SAVEABLE_FIELDS_WITHOUT_PUBLICATION_STATUS_UPDATE = [
  'name',
  'planningComment',
  'labels',
  'projectId',
  'sharePlanNumber',
  'maintenanceNumber',
  'comment',
  'shiftCategoryId',
] as const satisfies readonly (keyof SimplifiedShiftDrawerFormValue)[];

/**
 * Persists shift-level detail fields (name, OU, category, comments, etc.).
 */
@Injectable()
export class SimplifiedShiftDetailsSaveService {
  private readonly shiftsService = inject(ShiftsService);
  private readonly message = inject(NzMessageService);
  private readonly translate = inject(TranslateService);
  private readonly reloadAndSync = inject(
    SimplifiedShiftReloadAndTimelineSyncService,
  );

  areSaveableFieldsValid(
    formService: SimplifiedShiftDrawerFormService,
  ): boolean {
    const { controls } = formService.form;

    return (
      controls.name.valid &&
      controls.organizationalUnitId.valid &&
      controls.shiftCategoryId.valid &&
      controls.planningComment.valid
    );
  }

  hasDirtyShiftDetailFields(
    formService: SimplifiedShiftDrawerFormService,
  ): boolean {
    const fields = [
      ...SAVEABLE_FIELDS_WITH_PUBLICATION_STATUS_UPDATE,
      ...SAVEABLE_FIELDS_WITHOUT_PUBLICATION_STATUS_UPDATE,
    ] as const;

    return fields.some((field) => formService.form.controls[field].dirty);
  }

  async saveShiftDetails(params: {
    currentShift: SimplifiedDrawerShift;
    formValue: SimplifiedShiftDrawerFormValue;
    updatePublicationStatus: boolean;
    formService: SimplifiedShiftDrawerFormService;
    onSaveStateChange: SimplifiedShiftSaveStateChange;
    onShiftUpdated: (shift: ReloadedShift) => void;
    onUserIdSynced: (userId: string | null) => void;
    options?: { showSavingIndicator?: boolean };
  }): Promise<void> {
    const {
      currentShift,
      formValue,
      updatePublicationStatus,
      formService,
      onSaveStateChange,
      onShiftUpdated,
      onUserIdSynced,
      options,
    } = params;

    let publicationStatus: PublicationStatus;
    if (updatePublicationStatus) {
      publicationStatus = determinePublicationStatus(
        currentShift.activities,
        currentShift.publicationStatus,
      );
    } else {
      publicationStatus = currentShift.publicationStatus;
    }

    const shiftToUpdate = {
      id: currentShift.id,
      name: formValue.name.trim(),
      organizationalUnitId: formValue.organizationalUnitId as string,
      shiftCategoryId: formValue.shiftCategoryId as string,
      comment: formValue.comment,
      planningComment: formValue.planningComment,
      publicationStatus,
      labels: formValue.labels ?? [],
      projectId: formValue.projectId ?? null,
      sharePlanNumber: formValue.sharePlanNumber || null,
      maintenanceNumber: formValue.maintenanceNumber || null,
    };

    const showSavingIndicator = options?.showSavingIndicator ?? true;

    onSaveStateChange({
      isSaving: true,
      isSuccessfullySaved: false,
      showSavingIndicator,
    });

    try {
      await firstValueFrom(this.shiftsService.updateShift(shiftToUpdate));
      const reloadedShift =
        await this.reloadAndSync.reloadShiftAndUpdateTimeline(currentShift.id);

      if (reloadedShift) {
        onUserIdSynced(reloadedShift.userId ?? null);
        formService.patchFromShift(reloadedShift, {
          preserveTierTwoPromotion: true,
        });
        onShiftUpdated(reloadedShift);
      }

      onSaveStateChange({
        isSaving: false,
        isSuccessfullySaved: true,
        showSavingIndicator,
      });
    } catch (error) {
      captureException(error);
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
      onSaveStateChange({
        isSaving: false,
        isSuccessfullySaved: false,
        showSavingIndicator,
      });
    }
  }
}
